import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { Table, Container, Button, Badge, Modal, Row, Col, Form } from "react-bootstrap";

const LogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadLimit, setLoadLimit] = useState(50); // Initial batch size
  const [selectedLog, setSelectedLog] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Filters
  const [userIdFilter, setUserIdFilter] = useState("");
  const [orgIdFilter, setOrgIdFilter] = useState("");
  const [appIdFilter, setAppIdFilter] = useState("");
  const [logTypeFilter, setLogTypeFilter] = useState("all"); // all, alert, failure, success

  // Applications List
  const applications = [
    { "id": "0806090c-68d4-424a-91af-9858f903621a", "application_name": "chat_app" },
    { "id": "1ac086ec-0b58-4675-8189-7a338d11ab62", "application_name": "report_app" },
    { "id": "5caaa8f1-2f95-40fa-b023-471df36ff413", "application_name": "chrome_extension" },
    { "id": "8dbe2f1d-f9e4-4f83-94f9-ce4440b6a656", "application_name": "review_tool" },
    { "id": "9613148e-7053-4610-96a7-fcc87b047e57", "application_name": "figma_plugin" },
    { "id": "bcf44ae7-a37f-48af-a058-944f706389ef", "application_name": "cookie_consent_analyzer" },
    { "id": "e7f1ddbd-dee8-4c29-9cc2-3560cc35fece", "application_name": "observatory_dashboard" }
  ];

  const fetchOrganizations = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("organizations")
        .select("org_id, org_name")
        .order("org_name", { ascending: true });
      if (error) throw error;
      setOrganizations(data || []);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  }, []);

  const fetchApiLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from("api_logs")
        .select(`
          *,
          users(
            email,
            org_id,
            organizations(org_name)
          )
        `)
        .order("request_time", { ascending: false })
        .limit(loadLimit);

      // Apply Filters
      if (userIdFilter.trim()) {
        query = query.ilike("user_id", `%${userIdFilter.trim()}%`);
      }

      if (orgIdFilter) {
        query = query.eq("users.org_id", orgIdFilter);
      }

      if (appIdFilter) {
        query = query.eq("tool_id", appIdFilter);
      }

      if (logTypeFilter === "failure") {
        query = query.or("status_code.gte.400,return_status.ilike.%fail%");
      } else if (logTypeFilter === "alert") {
        query = query.ilike("return_status", "%alert%");
      } else if (logTypeFilter === "success") {
        query = query.lt("status_code", 400).not("return_status", "ilike", "%fail%").not("return_status", "ilike", "%alert%");
      }

      const { data, error } = await query;

      if (error) throw error;
      setLogs(data || []);
    } catch (error) {
      console.error("Error fetching API logs:", error);
    } finally {
      setIsLoading(false);
    }
  }, [loadLimit, userIdFilter, appIdFilter, logTypeFilter, orgIdFilter]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    fetchApiLogs();
  }, [fetchApiLogs]);

  const loadMoreLogs = () => {
    setLoadLimit(prevLimit => prevLimit + 50);
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setShowModal(true);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp).toLocaleString();
  };

  const getStatusBadge = (status_code, return_status) => {
    const isError = (status_code && status_code >= 400) || (return_status && return_status.toLowerCase().includes("fail"));
    const isAlert = return_status && return_status.toLowerCase().includes("alert");

    if (isError) return <Badge bg="danger">Failure</Badge>;
    if (isAlert) return <Badge bg="warning" text="dark">Alert</Badge>;
    return <Badge bg="success">Success</Badge>;
  };

  const getAppName = (tool_id) => {
    const app = applications.find(a => a.id === tool_id);
    return app ? app.application_name : (tool_id || "Unknown App");
  };

  return (
    <Container fluid className="px-4">
      <div className="d-flex justify-content-between align-items-center mt-4 mb-3">
        <h2>API Logs</h2>
        <Button variant="outline-secondary" size="sm" onClick={() => {
          setUserIdFilter("");
          setOrgIdFilter("");
          setAppIdFilter("");
          setLogTypeFilter("all");
        }}>
          Clear Filters
        </Button>
      </div>

      {/* Filters Row */}
      <Row className="mb-4 g-3 bg-light p-3 rounded border">
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small fw-bold">User ID</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search by User ID..."
              value={userIdFilter}
              onChange={(e) => setUserIdFilter(e.target.value)}
              size="sm"
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small fw-bold">Organization</Form.Label>
            <Form.Select
              value={orgIdFilter}
              onChange={(e) => setOrgIdFilter(e.target.value)}
              size="sm"
            >
              <option value="">All Organizations</option>
              {organizations.map(org => (
                <option key={org.org_id} value={org.org_id}>{org.org_name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small fw-bold">Application</Form.Label>
            <Form.Select
              value={appIdFilter}
              onChange={(e) => setAppIdFilter(e.target.value)}
              size="sm"
            >
              <option value="">All Applications</option>
              {applications.map(app => (
                <option key={app.id} value={app.id}>{app.application_name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small fw-bold">Log Type</Form.Label>
            <Form.Select
              value={logTypeFilter}
              onChange={(e) => setLogTypeFilter(e.target.value)}
              size="sm"
            >
              <option value="all">All Logs</option>
              <option value="failure">Failures</option>
              <option value="alert">Alerts</option>
              <option value="success">Success</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {isLoading && logs.length === 0 ? (
        <div className="text-center my-4 py-5">
           <div className="spinner-border text-primary mb-3" role="status"></div>
           <div>Loading logs...</div>
        </div>
      ) : (
        <>
          <Table striped hover responsive className="mt-2 border">
            <thead className="table-dark">
              <tr>
                <th>Time</th>
                <th>Organization</th>
                <th>Application</th>
                <th>API Name</th>
                <th>Method</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.length > 0 ? (
                logs.map((log) => (
                  <tr key={log.log_id}>
                    <td className="small text-nowrap">{formatTimestamp(log.request_time)}</td>
                    <td className="text-truncate" style={{ maxWidth: "150px" }}>
                      <span className="fw-bold">{log.users?.organizations?.org_name || "N/A"}</span>
                    </td>
                    <td><Badge bg="info" text="dark">{getAppName(log.tool_id)}</Badge></td>
                    <td className="fw-bold">
                      {log.api_prefix && <span className="text-muted small me-1">{log.api_prefix} :</span>}
                      {log.api_name}
                    </td>
                    <td><Badge bg="secondary">{log.request_method || "N/A"}</Badge></td>
                    <td>{getStatusBadge(log.status_code, log.return_status)}</td>
                    <td>
                      <Button 
                        size="sm" 
                        variant="primary" 
                        onClick={() => handleViewDetails(log)}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-5 text-muted">No logs found matching the filters</td>
                </tr>
              )}
            </tbody>
          </Table>
          
          {logs.length >= loadLimit && (
            <div className="text-center my-4">
              <Button variant="primary" onClick={loadMoreLogs}>
                {isLoading ? "Loading..." : "Load More"}
              </Button>
            </div>
          )}

          {/* Log Details Modal */}
          <Modal 
            show={showModal} 
            onHide={() => setShowModal(false)} 
            size="lg"
          >
            <Modal.Header closeButton>
              <Modal.Title>Log Entry Details</Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-light">
              {selectedLog && (
                <div className="bg-white p-3 border rounded shadow-sm">
                  <Row className="mb-4">
                    <Col sm={6}>
                      <h6>General Information</h6>
                      <hr />
                      <p><strong>Log ID:</strong> <span className="small text-muted font-monospace">{selectedLog.log_id}</span></p>
                      <hr />
                      <p><strong>Organization:</strong> <span className="text-dark fw-bold text-uppercase">{selectedLog.users?.organizations?.org_name || "N/A"}</span></p>
                      <p><strong>User Email:</strong> <span className="text-info">{selectedLog.users?.email || "N/A"}</span></p>
                      <p><strong>User ID:</strong> <span className="small text-muted font-monospace">{selectedLog.user_id || "N/A"}</span></p>
                      <hr />
                      <p><strong>Application:</strong> {getAppName(selectedLog.tool_id)}</p>
                      <p><strong>Request Time:</strong> {formatTimestamp(selectedLog.request_time)}</p>
                    </Col>
                    <Col sm={6}>
                      <h6>API Information</h6>
                      <hr />
                      <p><strong>API:</strong> <span className="text-primary fw-bold">{selectedLog.api_prefix ? `${selectedLog.api_prefix} : ` : ''}{selectedLog.api_name}</span></p>
                      <p><strong>Method:</strong> {selectedLog.request_method || "N/A"}</p>
                      <p><strong>Status:</strong> {getStatusBadge(selectedLog.status_code, selectedLog.return_status)}</p>
                      <p><strong>Status Code:</strong> {selectedLog.status_code || "N/A"}</p>
                      <p><strong>Processing Time:</strong> {selectedLog.processing_time}ms</p>
                    </Col>
                  </Row>

                  <div className="mb-3">
                    <h6>Request Body</h6>
                    <hr />
                    <pre className="bg-light text-dark p-3 rounded border" style={{ maxHeight: '200px', overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                      {selectedLog.request_body ? JSON.stringify(selectedLog.request_body, null, 2) : "No request body"}
                    </pre>
                  </div>

                  <div>
                    <h6>Message / Response</h6>
                    <hr />
                    <pre className="bg-dark text-light p-3 rounded" style={{ maxHeight: '300px', overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                      {selectedLog.message || selectedLog.return_status || "No message available"}
                    </pre>
                  </div>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </Container>
  );
};

export default LogsPage;
